import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Checkbox, Chip,
  List, ListItem, ListItemIcon, ListItemText, Typography,
} from '@mui/material';
import { customerHasChannel, isListedOnChannel } from './productUtils';

const CHANNEL_ROWS = [
  { id: 'ebay', title: 'eBay' },
  { id: 'shopify', title: 'Shopify' },
  { id: 'square', title: 'Square' },
  { id: 'etsy', title: 'Etsy' },
];

/**
 * Marketplace picker (web port of iOS ListingChannelsDrawerView): connected
 * channels are pre-checked and locked once actually posted; unconnected ones
 * link to Settings. Square and Etsy only appear once connected, as on iOS.
 *
 * Props: open, onClose, customerData, product, onList(Set<channelId>)
 */
export default function ListingChannelsDialog({ open, onClose, customerData, product, onList }) {
  const navigate = useNavigate();

  const rows = useMemo(() => CHANNEL_ROWS
    .map((row) => ({
      ...row,
      isConnected: customerHasChannel(customerData, row.id),
      isPosted: isListedOnChannel(product, row.id) || (row.id === 'square' && !!product?.importedFromSquare),
    }))
    .filter((row) => row.isConnected || row.id === 'ebay' || row.id === 'shopify'),
  [customerData, product]);

  const [selected, setSelected] = useState(new Set());
  useEffect(() => {
    if (open) setSelected(new Set(rows.filter((r) => r.isConnected && !r.isPosted).map((r) => r.id)));
  }, [open, rows]);

  const newCount = rows.filter((r) => selected.has(r.id) && !r.isPosted).length;

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
        List Item
        <Typography variant="body2" color="text.secondary">Choose where to publish your item</Typography>
      </DialogTitle>
      <DialogContent>
        <List dense>
          {rows.map((row) => (
            <ListItem
              key={row.id}
              disableGutters
              secondaryAction={
                row.isPosted ? <Chip label="Live" size="small" color="success" />
                  : row.isConnected ? <Chip label="Connected" size="small" color="success" variant="outlined" />
                    : (
                      <Button size="small" onClick={() => { onClose(); navigate('/settings'); }}>
                        Connect
                      </Button>
                    )
              }
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Checkbox
                  edge="start"
                  size="small"
                  disabled={!row.isConnected || row.isPosted}
                  checked={row.isPosted || selected.has(row.id)}
                  onChange={() => toggle(row.id)}
                />
              </ListItemIcon>
              <ListItemText primary={row.title} primaryTypographyProps={{ fontWeight: 600 }} />
            </ListItem>
          ))}
        </List>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ textAlign: 'center' }}>
          You can always add or remove marketplaces later in Settings.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          disabled={newCount === 0}
          onClick={() => {
            onList(new Set(rows.filter((r) => selected.has(r.id) && !r.isPosted).map((r) => r.id)));
            onClose();
          }}
        >
          {newCount > 0 ? `List to ${newCount} Marketplace${newCount === 1 ? '' : 's'}` : 'Select a marketplace'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
